import {
    Autocomplete,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import { LocalizationObject } from '../../../redux/slices/userSlice'
import { useTranslation } from 'react-i18next'
import { getAllTimezoneOptions } from '../../../utils/TimezoneOptions'
import LanguageSwitcher from '../../Common/LanguageSwitcher'
import theme from '../../../theme/theme'

interface LocalizationPreferencesProps {
    initialPreferences: LocalizationObject
    handleLocalizationChange: (
        key: keyof LocalizationObject,
        value: string | null,
    ) => void
    disabled?: boolean
}

const LocalizationPreferences = ({
    initialPreferences,
    handleLocalizationChange,
    disabled,
}: LocalizationPreferencesProps): JSX.Element => {
    const { t } = useTranslation()

    return (
        <Box>
            <Typography
                variant="h6"
                component="h2"
                sx={{
                    fontSize: theme.typography.h6,
                    textWrap: 'nowrap',
                    marginBottom: '15px',
                }}
            >
                {t('localizationPreferences')}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '30px',
                }}
            >
                <FormControl fullWidth variant="outlined" disabled={disabled}>
                    <InputLabel
                        id="date-format-label"
                        sx={{
                            backgroundColor: 'white',
                            px: 1,
                            borderRadius: 1,
                        }}
                    >
                        {t('dateFormat')}
                    </InputLabel>
                    <Select
                        labelId="date-format-label"
                        id="date-format-select"
                        value={initialPreferences.dateFormat || ''} // Controlled component: value should never be undefined
                        label="Date Format"
                        onChange={(e) =>
                            handleLocalizationChange(
                                'dateFormat',
                                e.target.value as string,
                            )
                        }
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="DD/MM/YYYY">
                            {t('DD/MM/YYYY')}
                        </MenuItem>
                        <MenuItem value="MM/DD/YYYY">
                            {t('MM/DD/YYYY')}
                        </MenuItem>
                        <MenuItem value="YYYY/MM/DD">
                            {t('YYYY/MM/DD')}
                        </MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth variant="outlined" disabled={disabled}>
                    <InputLabel
                        id="time-format-label"
                        sx={{
                            backgroundColor: 'white',
                            px: 1,
                            borderRadius: 1,
                        }}
                    >
                        {t('timeFormat')}
                    </InputLabel>
                    <Select
                        labelId="time-format-label"
                        id="time-format-select"
                        value={initialPreferences.timeFormat || ''} // Controlled component
                        label="Time Format"
                        onChange={(e) =>
                            handleLocalizationChange(
                                'timeFormat',
                                e.target.value as string,
                            )
                        }
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="HH:mm">HH:mm (24-hour)</MenuItem>
                        <MenuItem value="HH:mm:ss">HH:mm:ss (24-hour)</MenuItem>
                        <MenuItem value="hh:mm A">hh:mm</MenuItem>
                        <MenuItem value="hh:mm:ss A">hh:mm:ss</MenuItem>
                    </Select>
                </FormControl>
                <Autocomplete
                    options={getAllTimezoneOptions()}
                    id="timezone-format-select"
                    getOptionLabel={(option) => option.label}
                    disabled={disabled}
                    value={
                        getAllTimezoneOptions().find(
                            (opt) => opt.value === initialPreferences.timeZone,
                        ) || null
                    }
                    onChange={(_, newValue, reason) => {
                        if (reason === 'clear' || !newValue) {
                            handleLocalizationChange('timeZone', null)
                        } else {
                            handleLocalizationChange('timeZone', newValue.value)
                        }
                    }}
                    filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                            option.label
                                .toLowerCase()
                                .includes(inputValue.toLowerCase()),
                        )
                    }
                    slotProps={{
                        listbox: {
                            style: {
                                maxHeight: 20 * 15,
                                overflowY: 'auto',
                            },
                        },
                        clearIndicator: {
                            id: 'timezone-clear-button',
                        },
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('timeZone')}
                            variant="outlined"
                            helperText={
                                !initialPreferences.timeZone
                                    ? t('leaveEmptyForBrowserTimezone')
                                    : ''
                            }
                            /* UBQ-5543: neutral helper colour (matches the Send Invite
                               footer) at the standard 12px helper size — FormHelperText
                               alone renders 10.5px against the 14px theme base. */
                            slotProps={{
                                formHelperText: {
                                    sx: { fontSize: 12 },
                                },
                            }}
                        />
                    )}
                    sx={{ borderRadius: 2, minWidth: 350 }}
                />
                <LanguageSwitcher
                    value={initialPreferences.language}
                    onChange={(lang) =>
                        handleLocalizationChange('language', lang)
                    }
                    disabled={disabled}
                />
            </Box>
        </Box>
    )
}

export default LocalizationPreferences
